import { RankedEstateHideAction } from "@/components/index/fragments/RankedEstateSlide";
import { Button } from "@/components/ui/Button";
import { useLanguage } from "@/context/LanguageContext";
import { formatCzkPrice } from "@/lib/price";
import type { PublicWatchDogOffer } from "@/types";
import { StarIcon } from "@heroicons/react/24/solid";
import Image from "next/image";
import { useEffect, useState } from "react";

type RankedEstateFavoriteSlideProps = {
  offer: PublicWatchDogOffer;
  city: string;
};

export function RankedEstateFavoriteSlide({
  offer,
  city,
}: RankedEstateFavoriteSlideProps) {
  const { language } = useLanguage();
  const fallbackImage = "/image/membership/star4.png";
  const [imgSrc, setImgSrc] = useState(
    offer.mainPicUrl?.includes("empty.gif")
      ? fallbackImage
      : offer.mainPicUrl || fallbackImage,
  );
  const copy = {
    cs: {
      title: "Třešnička na závěr",
      description: "Není nejdražší, ale zaujal mě svým stylem.",
    },
    en: {
      title: "A final favorite",
      description:
        "It may not be the most expensive, but its style caught my eye.",
    },
  }[language];

  useEffect(() => {
    setImgSrc(
      offer.mainPicUrl?.includes("empty.gif")
        ? fallbackImage
        : offer.mainPicUrl || fallbackImage,
    );
  }, [offer.mainPicUrl]);

  return (
    <div className="relative size-full overflow-hidden rounded-2xl bg-slate-950 shadow-lg">
      <Image
        src={imgSrc}
        alt={offer.header || "Nabídka nemovitosti"}
        fill
        loading="lazy"
        sizes="(min-width: 1024px) 50vw, 100vw"
        className="object-cover"
        onError={() => setImgSrc(fallbackImage)}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/15 to-black/90" />

      <div className="absolute inset-0 flex flex-col justify-end p-6 text-white sm:p-8">
        <div>
          <h3 className="text-4xl font-black leading-none tracking-tight drop-shadow-lg sm:text-5xl">
            {copy.title}
          </h3>
          <p className="mt-4 max-w-lg text-md font-semibold leading-snug text-white/90 drop-shadow-md sm:text-xl">
            {copy.description}
          </p>
          <p className="mt-4 max-w-lg text-md font-semibold leading-snug text-white/90 drop-shadow-md sm:text-xl">
            {formatCzkPrice(offer.price)}
          </p>
          <p className="mt-5 text-base font-semibold uppercase tracking-[0.16em] text-white/70">
            {city}
          </p>
        </div>
      </div>
    </div>
  );
}

type RankedEstateFavoriteActionsProps = {
  onUnfavorite: () => void | Promise<void>;
  onHide: () => void | Promise<void>;
};

export function RankedEstateFavoriteActions({
  onUnfavorite,
  onHide,
}: RankedEstateFavoriteActionsProps) {
  const { language } = useLanguage();
  const action =
    language === "cs" ? "Odebrat z oblíbených" : "Remove from favorites";

  return (
    <div className="grid grid-cols-2 gap-3 [&_button]:whitespace-normal [&_button]:px-2 [&_button]:text-xs [&_button]:leading-tight sm:[&_button]:text-sm">
      <Button
        type="button"
        variant="outline"
        className="h-12 w-full rounded-xl font-semibold text-softiq-secondary-shade-800 hover:bg-softiq-secondary-shade-100 hover:text-softiq-primary dark:text-softiq-secondary dark:hover:bg-softiq-secondary/10"
        onClick={onUnfavorite}
      >
        <StarIcon className="size-4 text-yellow-500" />
        {action}
      </Button>
      <RankedEstateHideAction onHide={onHide} />
    </div>
  );
}
